import { LocalDate } from '@js-joda/core';
import { StopeDataDto } from 'providers/api';

export interface StopeShapeDateRange {
  startEpochDay: number;
  endEpochDay: number;
}

type StopeDateData = Pick<StopeDataDto, 'startDate' | 'endDate' | 'stopeGeometryFile'>;
type StopeLike = { data?: Partial<StopeDateData> | null };

export const toEpochDay = (value: unknown): number | null => {
  if (!value) return null;

  const localDateLike = value as { toEpochDay?: () => number; toString?: () => string };
  if (typeof localDateLike.toEpochDay === 'function') {
    const epochDay = localDateLike.toEpochDay();
    return Number.isFinite(epochDay) ? epochDay : null;
  }

  const text = typeof value === 'string' ? value : localDateLike.toString?.();
  if (!text) return null;

  const isoDate = text.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;

  try {
    return LocalDate.parse(isoDate).toEpochDay();
  } catch {
    return null;
  }
};

export const formatEpochDay = (epochDay: number): string => LocalDate.ofEpochDay(epochDay).toString();

export const getStopeDateInterval = (stopeData?: Partial<StopeDateData> | null): StopeShapeDateRange | null => {
  const start = toEpochDay(stopeData?.startDate);
  const end = toEpochDay(stopeData?.endDate);

  if (start === null && end === null) return null;

  const resolvedStart = start ?? end!;
  const resolvedEnd = end ?? start!;

  return {
    startEpochDay: Math.min(resolvedStart, resolvedEnd),
    endEpochDay: Math.max(resolvedStart, resolvedEnd),
  };
};

export const stopeIntersectsDateRange = (
  stopeData: Partial<StopeDateData> | undefined | null,
  range: StopeShapeDateRange | null,
): boolean => {
  if (!range) return true;

  const interval = getStopeDateInterval(stopeData);
  if (!interval) return false;

  return interval.endEpochDay >= range.startEpochDay
    && interval.startEpochDay <= range.endEpochDay;
};

export const getStopeShapeDateDomain = (
  stopes: Record<string, StopeLike[]> | undefined | null,
): StopeShapeDateRange | null => {
  let minEpochDay = Number.POSITIVE_INFINITY;
  let maxEpochDay = Number.NEGATIVE_INFINITY;

  Object.values(stopes ?? {}).flat().forEach((stope) => {
    if (!stope?.data?.stopeGeometryFile?.path) return;

    const interval = getStopeDateInterval(stope.data);
    if (!interval) return;

    minEpochDay = Math.min(minEpochDay, interval.startEpochDay);
    maxEpochDay = Math.max(maxEpochDay, interval.endEpochDay);
  });

  if (!Number.isFinite(minEpochDay) || !Number.isFinite(maxEpochDay)) return null;

  return {
    startEpochDay: minEpochDay,
    endEpochDay: maxEpochDay,
  };
};
